import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { ArrowPathIcon, BoltIcon, ExclamationTriangleIcon, HandThumbUpIcon, SparklesIcon } from '@heroicons/react/24/outline';

const CASE_STATUS_OPTIONS = ['', 'ABERTO', 'PROMESSA', 'EM_NEGOCIACAO', 'RECUPERADO', 'ENCERRADO', 'CANCELADO'];
const SOURCE_MODULE_OPTIONS = ['', 'CORE', 'SOLAR'];
const ACTION_STATUS_OPTIONS = ['CONCLUIDA', 'ENVIADA', 'FALHA', 'PULADA', 'CANCELADA'];
const PROMISE_STATUS_OPTIONS = ['CUMPRIDA', 'QUEBRADA', 'CANCELADA'];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = (value) =>
  toNumber(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDateTime = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString('pt-BR');
};

const formatDate = (value) => {
  if (!value) return '-';
  const raw = String(value);
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return raw;
  return dt.toLocaleDateString('pt-BR');
};

const caseBadge = (status) => {
  if (status === 'RECUPERADO') return 'badge-success';
  if (status === 'PROMESSA') return 'badge-warning';
  if (status === 'EM_NEGOCIACAO') return 'badge-info';
  if (status === 'CANCELADO' || status === 'ENCERRADO') return 'badge-danger';
  return 'badge-info';
};

const riskTone = (riskScore) => {
  const risk = toNumber(riskScore);
  if (risk >= 75) return 'bg-rose-50';
  if (risk >= 45) return 'bg-amber-50';
  return '';
};

const emptyActionForm = {
  canal: 'INTERNO',
  action_type: 'ALERTA',
  direction: 'OUTBOUND',
  status: 'CONCLUIDA',
  destinatario: '',
  subject: '',
  message_body: '',
  response_summary: '',
};

const emptyPromiseForm = {
  due_date: '',
  promised_amount: '',
  notes: '',
};

const CollectionsRecoveryPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    source_module: '',
    q: '',
    min_risk: '',
  });

  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionForm, setActionForm] = useState(emptyActionForm);
  const [promiseForm, setPromiseForm] = useState(emptyPromiseForm);

  const loadDashboard = async () => {
    try {
      const { data } = await axios.get('/api/collections/dashboard');
      setDashboard(data || null);
    } catch (error) {
      if (error?.response?.status === 404) return;
      toast.error(error.response?.data?.error || 'Erro ao carregar indicadores');
    }
  };

  const loadCases = async () => {
    setLoading(true);
    try {
      const params = {
        pageSize: 120,
      };
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) params[key] = value;
      });
      const { data } = await axios.get('/api/collections/cases', { params });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar casos');
    } finally {
      setLoading(false);
    }
  };

  const loadCaseDetail = async (caseId) => {
    if (!caseId) return;
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`/api/collections/cases/${caseId}`);
      setSelectedCase(data || null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar detalhe do caso');
    } finally {
      setDetailLoading(false);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post('/api/collections/engine/sync', {
        source_module: filters.source_module || '',
      });
      const summary = response?.data?.summary || {};
      toast.success(
        `Motor atualizado: ${summary.cases_created || 0} novos casos, ${summary.actions_created || 0} acoes geradas`
      );
      await Promise.all([loadDashboard(), loadCases()]);
      if (selectedCaseId) {
        await loadCaseDetail(selectedCaseId);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao sincronizar motor');
    } finally {
      setSyncing(false);
    }
  };

  const runDispatchNow = async () => {
    setDispatching(true);
    try {
      const { data } = await axios.post('/api/collections/engine/dispatch-now', { limit: 200 });
      const summary = data?.summary || {};
      toast.success(
        `Disparo concluido: ${summary.sent || 0} enviada(s), ${summary.failed || 0} falha(s), ${summary.canceled || 0} cancelada(s)`
      );
      await Promise.all([loadDashboard(), loadCases()]);
      if (selectedCaseId) {
        await loadCaseDetail(selectedCaseId);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao executar dispatcher');
    } finally {
      setDispatching(false);
    }
  };

  const executeAction = async (actionId, status = 'CONCLUIDA') => {
    if (!selectedCaseId || !actionId) return;
    try {
      await axios.post(`/api/collections/cases/${selectedCaseId}/actions/${actionId}/execute`, {
        status,
      });
      toast.success('Acao atualizada');
      await Promise.all([loadDashboard(), loadCases(), loadCaseDetail(selectedCaseId)]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao executar acao');
    }
  };

  const saveManualAction = async () => {
    if (!selectedCaseId) return;
    try {
      await axios.post(`/api/collections/cases/${selectedCaseId}/actions/log`, actionForm);
      toast.success('Interacao registrada');
      setActionForm(emptyActionForm);
      await Promise.all([loadDashboard(), loadCases(), loadCaseDetail(selectedCaseId)]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao registrar interacao');
    }
  };

  const savePromise = async () => {
    if (!selectedCaseId) return;
    if (!promiseForm.due_date || !promiseForm.promised_amount) {
      toast.error('Informe data e valor da promessa');
      return;
    }

    try {
      await axios.post(`/api/collections/cases/${selectedCaseId}/promises`, {
        due_date: promiseForm.due_date,
        promised_amount: Number(promiseForm.promised_amount),
        notes: promiseForm.notes || null,
      });
      toast.success('Promessa registrada');
      setPromiseForm(emptyPromiseForm);
      await Promise.all([loadDashboard(), loadCases(), loadCaseDetail(selectedCaseId)]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao registrar promessa');
    }
  };

  const changePromiseStatus = async (promiseId, status) => {
    try {
      await axios.post(`/api/collections/promises/${promiseId}/status`, { status });
      toast.success('Promessa atualizada');
      await Promise.all([loadDashboard(), loadCases(), loadCaseDetail(selectedCaseId)]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar promessa');
    }
  };

  const closeCase = async (status) => {
    if (!selectedCaseId) return;
    try {
      await axios.post(`/api/collections/cases/${selectedCaseId}/close`, {
        status,
        closed_reason: `Encerrado manualmente (${status})`,
      });
      toast.success('Caso encerrado');
      await Promise.all([loadDashboard(), loadCases(), loadCaseDetail(selectedCaseId)]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao encerrar caso');
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    loadCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.source_module, filters.q, filters.min_risk]);

  useEffect(() => {
    if (selectedCaseId) {
      loadCaseDetail(selectedCaseId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCaseId]);

  const cards = useMemo(() => {
    return [
      {
        label: 'Carteira em risco',
        value: formatMoney(dashboard?.carteira_aberta || 0),
        hint: `${dashboard?.casos_abertos || 0} casos abertos`,
        tone: 'border-rose-200 bg-rose-50 text-rose-900',
      },
      {
        label: 'Valor recuperado',
        value: formatMoney(dashboard?.valor_recuperado || 0),
        hint: `${dashboard?.casos_recuperados || 0} casos recuperados`,
        tone: 'border-emerald-200 bg-emerald-50 text-emerald-900',
      },
      {
        label: 'Promessas ativas',
        value: String(dashboard?.promessas_abertas || 0),
        hint: `${dashboard?.promessas_em_atraso || 0} em atraso`,
        tone: 'border-amber-200 bg-amber-50 text-amber-900',
      },
      {
        label: 'Acoes para hoje',
        value: String(dashboard?.pendentes_vencidas || 0),
        hint: `${dashboard?.pendentes || 0} pendentes no total`,
        tone: 'border-sky-200 bg-sky-50 text-sky-900',
      },
    ];
  }, [dashboard]);

  return (
    <div className="animate-fade-in space-y-6">
      <section className="rounded-2xl border border-slate-700 bg-gradient-to-r from-slate-900 via-cyan-900 to-emerald-900 p-6 text-white shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">Cobranca inteligente</p>
            <h1 className="text-3xl font-bold mt-1">Motor de Recuperacao de Receita</h1>
            <p className="mt-2 text-cyan-100 max-w-3xl">
              Priorize os casos com maior risco financeiro, execute contatos em cadencia e transforme atraso em caixa previsivel.
            </p>
          </div>
          <button className="btn-primary" onClick={runSync} disabled={syncing}>
            <ArrowPathIcon className={`h-5 w-5 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Atualizando motor...' : 'Atualizar motor agora'}
          </button>
          <button className="btn-secondary" onClick={runDispatchNow} disabled={dispatching}>
            {dispatching ? 'Disparando...' : 'Executar disparo agora'}
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <div key={card.label} className={`rounded-xl border p-4 shadow-sm ${card.tone}`}>
            <p className="text-xs uppercase tracking-wide opacity-80">{card.label}</p>
            <p className="mt-1 text-2xl font-semibold">{card.value}</p>
            <p className="mt-1 text-xs opacity-80">{card.hint}</p>
          </div>
        ))}
      </section>

      <div className="card">
        <div className="card-body p-3">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:items-end">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select className="form-select text-sm py-1.5" value={filters.status} onChange={(e) => setFilters((old) => ({ ...old, status: e.target.value }))}>
                {CASE_STATUS_OPTIONS.map((option) => (
                  <option key={option || 'all'} value={option}>
                    {option || 'Todos'}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Modulo origem</label>
              <select className="form-select text-sm py-1.5" value={filters.source_module} onChange={(e) => setFilters((old) => ({ ...old, source_module: e.target.value }))}>
                {SOURCE_MODULE_OPTIONS.map((option) => (
                  <option key={option || 'all'} value={option}>
                    {option || 'Todos'}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Risco minimo</label>
              <input className="form-input text-sm py-1.5" type="number" min="0" max="100" value={filters.min_risk} onChange={(e) => setFilters((old) => ({ ...old, min_risk: e.target.value }))} />
            </div>
            <div className="md:col-span-4">
              <label className="block text-xs font-medium text-gray-600 mb-1">Busca</label>
              <input className="form-input text-sm py-1.5" value={filters.q} onChange={(e) => setFilters((old) => ({ ...old, q: e.target.value }))} placeholder="Cliente ou id da cobranca" />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button className="btn-secondary text-sm py-1.5 px-3" onClick={() => setFilters({ status: '', source_module: '', q: '', min_risk: '' })}>
                Limpar filtros
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between gap-3">
          <span>Casos de recuperacao ({items.length})</span>
          <div className="flex items-center gap-3">
            {loading && <div className="loader h-5 w-5" />}
            <span className="text-xs text-gray-500">Clique em um caso para operar</span>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Modulo</th>
                  <th>Status</th>
                  <th>Risco</th>
                  <th>Em aberto</th>
                  <th>Proxima acao</th>
                  <th>Promessa</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className={riskTone(item.risco_score)}>
                    <td className="font-medium">{item.customer_nome || '-'}</td>
                    <td>{item.source_module || '-'}</td>
                    <td>
                      <span className={caseBadge(item.status)}>{item.status}</span>
                    </td>
                    <td className="font-semibold">{toNumber(item.risco_score).toFixed(1)}</td>
                    <td className="font-semibold text-rose-800">{formatMoney(item.amount_open || 0)}</td>
                    <td>{formatDateTime(item.next_action_scheduled_for || item.next_action_at)}</td>
                    <td>
                      {item.open_promise_id ? (
                        <span className="text-amber-700 font-medium">
                          {formatDate(item.open_promise_due_date)} - {formatMoney(item.open_promise_amount || 0)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td>
                      <button className="btn-secondary text-xs py-1.5 px-2" onClick={() => setSelectedCaseId(item.id)}>
                        Operar
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-gray-500">
                      Nenhum caso encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedCaseId && (
        <div className="fixed inset-0 bg-gray-900/50 z-50 flex justify-end">
          <div className="h-full w-full max-w-2xl bg-white shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 z-10 flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Painel de operacao</p>
                <h2 className="text-xl font-semibold">{selectedCase?.customer_nome || 'Caso de cobranca'}</h2>
              </div>
              <button className="btn-secondary" onClick={() => { setSelectedCaseId(''); setSelectedCase(null); }}>
                Fechar
              </button>
            </div>

            {detailLoading || !selectedCase ? (
              <div className="p-10 flex justify-center">
                <div className="loader h-10 w-10" />
              </div>
            ) : (
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                    <p className="text-xs uppercase text-rose-700">Em aberto</p>
                    <p className="text-xl font-semibold text-rose-900">{formatMoney(selectedCase.amount_open || 0)}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs uppercase text-slate-600">Risco</p>
                    <p className="text-xl font-semibold text-slate-900">{toNumber(selectedCase.risco_score).toFixed(1)}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className={caseBadge(selectedCase.status)}>{selectedCase.status}</span>
                  <span className="badge-info">{selectedCase.source_module}</span>
                  <span className="badge-warning">Venc.: {formatDate(selectedCase.data_vencimento)}</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button className="btn-success" onClick={() => closeCase('RECUPERADO')}>
                    <HandThumbUpIcon className="h-5 w-5 mr-2" />
                    Marcar recuperado
                  </button>
                  <button className="btn-danger" onClick={() => closeCase('ENCERRADO')}>
                    <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                    Encerrar caso
                  </button>
                </div>

                <div className="card">
                  <div className="card-header flex items-center gap-2">
                    <SparklesIcon className="h-5 w-5 text-amber-500" />
                    Registrar promessa de pagamento
                  </div>
                  <div className="card-body grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input className="form-input" type="date" value={promiseForm.due_date} onChange={(e) => setPromiseForm((old) => ({ ...old, due_date: e.target.value }))} />
                    <input className="form-input" type="number" step="0.01" placeholder="Valor prometido" value={promiseForm.promised_amount} onChange={(e) => setPromiseForm((old) => ({ ...old, promised_amount: e.target.value }))} />
                    <button className="btn-primary" onClick={savePromise}>Salvar promessa</button>
                    <textarea className="form-textarea md:col-span-3" rows={2} placeholder="Notas da negociacao" value={promiseForm.notes} onChange={(e) => setPromiseForm((old) => ({ ...old, notes: e.target.value }))} />
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">Acoes pendentes</div>
                  <div className="card-body space-y-3">
                    {(selectedCase.actions || []).filter((action) => action.status === 'PENDENTE').map((action) => (
                      <div key={action.id} className="rounded-lg border border-gray-200 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="font-medium">{action.action_type} via {action.canal}</p>
                          <p className="text-xs text-gray-500">{formatDateTime(action.scheduled_for)}</p>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">{action.subject || action.message_body || '-'}</p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {ACTION_STATUS_OPTIONS.map((status) => (
                            <button key={status} className="btn-secondary text-xs py-1.5 px-2" onClick={() => executeAction(action.id, status)}>
                              {status}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    {(selectedCase.actions || []).filter((action) => action.status === 'PENDENTE').length === 0 && (
                      <p className="text-sm text-gray-500">Sem acoes pendentes no momento.</p>
                    )}
                  </div>
                </div>

                <div className="card">
                  <div className="card-header flex items-center gap-2">
                    <BoltIcon className="h-5 w-5 text-sky-500" />
                    Registrar interacao manual
                  </div>
                  <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-3">
                    <select className="form-select" value={actionForm.canal} onChange={(e) => setActionForm((old) => ({ ...old, canal: e.target.value }))}>
                      {['INTERNO', 'WHATSAPP', 'EMAIL', 'SMS'].map((value) => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </select>
                    <select className="form-select" value={actionForm.action_type} onChange={(e) => setActionForm((old) => ({ ...old, action_type: e.target.value }))}>
                      {['ALERTA', 'REMINDER', 'NEGOCIAR', 'ULTIMATO'].map((value) => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </select>
                    <input className="form-input md:col-span-2" placeholder="Assunto" value={actionForm.subject} onChange={(e) => setActionForm((old) => ({ ...old, subject: e.target.value }))} />
                    <textarea className="form-textarea md:col-span-2" rows={3} placeholder="Resumo da conversa" value={actionForm.response_summary} onChange={(e) => setActionForm((old) => ({ ...old, response_summary: e.target.value }))} />
                    <button className="btn-primary md:col-span-2" onClick={saveManualAction}>Registrar interacao</button>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">Historico de promessas</div>
                  <div className="card-body space-y-2">
                    {(selectedCase.promises || []).map((promise) => (
                      <div key={promise.id} className="rounded-lg border border-gray-200 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="font-medium">{formatDate(promise.due_date)} - {formatMoney(promise.promised_amount || 0)}</p>
                          <span className={promise.status === 'ABERTA' ? 'badge-warning' : promise.status === 'CUMPRIDA' ? 'badge-success' : 'badge-danger'}>
                            {promise.status}
                          </span>
                        </div>
                        {promise.status === 'ABERTA' && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {PROMISE_STATUS_OPTIONS.map((status) => (
                              <button key={status} className="btn-secondary text-xs py-1.5 px-2" onClick={() => changePromiseStatus(promise.id, status)}>
                                {status}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {(selectedCase.promises || []).length === 0 && <p className="text-sm text-gray-500">Nenhuma promessa registrada.</p>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionsRecoveryPage;
